import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendEmailVerification, sendPasswordResetEmail, signOut, GoogleAuthProvider, signInWithPopup, updateProfile, deleteUser } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { app } from '@lib/firebase';  // Ensure this alias '@lib/firebase' is correctly configured in your Vite config.

const auth = getAuth(app);
const db = getFirestore(app);
const googleProvider = new GoogleAuthProvider();

/**
 * 🔹 Simpan user ke Firestore
 */
const saveUserToFirestore = async (user) => {
  if (!user) return;

  try {
    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      await setDoc(userRef, {
        uid: user.uid,
        name: user.displayName || "",
        email: user.email,
        photoURL: user.photoURL || "",
        createdAt: serverTimestamp(),
        role: "user", // default role
      });
    } else {
    }
  } catch (error) {
    console.error("❌ Gagal menyimpan user:", error.message);
  }
};

/**
 * 🔹 Register user & kirim email verifikasi
 */
export const registerAndSendVerification = async (email, name, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    await updateProfile(user, { displayName: name });
    await sendEmailVerification(user);

    await saveUserToFirestore(user);

    console.log(`Email verifikasi dikirim ke: ${email}`);
    return { message: "Silakan cek email Anda untuk verifikasi sebelum login.", user };
  } catch (error) {
    console.error("Error register:", error.message);
    throw new Error(error.message);
  }
};

/**
 * 🔹 Cek apakah email sudah diverifikasi & hapus akun jika tidak diverifikasi dalam 2 menit
 */
export const checkVerificationAndDelete = async () => {
  const user = auth.currentUser;
  if (!user) return;

  await user.reload();
  if (!user.emailVerified) {
    try {
      await deleteUser(user);
      console.log("Akun dihapus karena tidak diverifikasi dalam 2 menit.");
    } catch (error) {
      console.error("Gagal menghapus akun:", error);
    }
  }
};

/**
 * 🔹 Kirim ulang email verifikasi
 */
export const sendVerificationEmail = async (user) => {
  if (!user) throw new Error("User tidak ditemukan.");
  try {
    await sendEmailVerification(user);
    return "Email verifikasi telah dikirim. Periksa inbox Anda.";
  } catch (error) {
    console.error("Error mengirim email verifikasi:", error.message);
    throw new Error(error.message);
  }
};

/**
 * 🔹 Cek apakah email sudah diverifikasi
 */
export const checkEmailVerification = async () => {
  if (!auth.currentUser) return false;
  await auth.currentUser.reload();
  return auth.currentUser.emailVerified;
};

/**
 * 🔹 Login dengan email & password
 */
export const login = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    if (user) {
      await user.reload();
      if (!user.emailVerified) {
        throw new Error("Email belum diverifikasi. Silakan cek email Anda.");
      }
      await saveUserToFirestore(user);
    }
    return user;
  } catch (error) {
    console.error("Error login:", error.message);
    throw new Error(error.message);
  }
};

/**
 * 🔹 Login dengan Google
 */
export const loginWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    if (user) {
      await user.reload();
      await saveUserToFirestore(user);
      return user;
    }
  } catch (error) {
    console.error("Error login Google:", error.message);
    throw new Error(error.message);
  }
};

/**
 * 🔹 Logout user
 */
export const logout = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error("Error logout:", error.message);
    throw new Error(error.message);
  }
};

/**
 * 🔹 Update nama profil setelah registrasi
 */
export const updateUserProfile = async (name) => {
  try {
    if (!auth.currentUser) throw new Error("User tidak ditemukan.");
    await updateProfile(auth.currentUser, { displayName: name });
  } catch (error) {
    console.error("Error update profile:", error.message);
    throw new Error(error.message);
  }
};

/**
 * 🔹 Refresh user data setelah verifikasi
 */
export const refreshUser = async () => {
  if (auth.currentUser) {
    await auth.currentUser.reload();
    return auth.currentUser;
  }
  return null;
};

/**
 * 🔹 Reset password
 */
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return "Email reset password telah dikirim. Periksa inbox Anda.";
  } catch (error) {
    console.error("Error reset password:", error.message);
    throw new Error(error.message);
  }
};
